import React, { useState } from 'react';
import { Input, Button, Modal, Spin } from 'antd';
import { useProxySettingForm } from './hooks/useProxySettingForm';

const P9105Form: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    empCode, setEmpCode,
    proxy1, setProxy1, proxy2, setProxy2,
    proxy3, setProxy3, proxy4, setProxy4,
    proxy5, setProxy5,
    dept1, setDept1, dept2, setDept2,
    dept3, setDept3, dept4, setDept4,
    dept5, setDept5,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
  } = useProxySettingForm();

  const [searchText, setSearchText] = useState('');

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除員工「${current.f905_01}」的代理人設定？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleQuickSearch = () => {
    if (searchText.trim()) {
      quickSearch(searchText.trim());
    }
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  return (
    <Spin spinning={loading}>
      <div className="max-w-3xl mx-auto p-6">
        {/* Title */}
        <h2 className="text-xl font-bold mb-4">
          代理人設定 (pw9105f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick Search Bar */}
        <div className="flex items-center gap-2 mb-4">
          <Input
            placeholder="輸入員工代號快速查詢"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleQuickSearch}
            disabled={isEditing}
            style={{ width: 300 }}
            maxLength={8}
          />
          <Button onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Navigation Bar */}
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Form Fields */}
        <div className="space-y-4 mb-6">
          {/* 員工代號 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">員工代號 (F905_01)</label>
            <Input
              value={empCode}
              onChange={e => setEmpCode(e.target.value)}
              maxLength={8}
              disabled={mode !== 'add'}
              style={{ width: 200 }}
              placeholder="最多8字元"
            />
          </div>

          {/* 代理人 1~5 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">代理人設定</label>
            <div className="grid grid-cols-5 gap-2">
              <div>
                <span className="text-xs text-gray-500">代理人1 (F905_11)</span>
                <Input value={proxy1} onChange={e => setProxy1(e.target.value)}
                  maxLength={8} disabled={!isEditing} placeholder="代理人1" />
              </div>
              <div>
                <span className="text-xs text-gray-500">代理人2 (F905_12)</span>
                <Input value={proxy2} onChange={e => setProxy2(e.target.value)}
                  maxLength={8} disabled={!isEditing} placeholder="代理人2" />
              </div>
              <div>
                <span className="text-xs text-gray-500">代理人3 (F905_13)</span>
                <Input value={proxy3} onChange={e => setProxy3(e.target.value)}
                  maxLength={8} disabled={!isEditing} placeholder="代理人3" />
              </div>
              <div>
                <span className="text-xs text-gray-500">代理人4 (F905_14)</span>
                <Input value={proxy4} onChange={e => setProxy4(e.target.value)}
                  maxLength={8} disabled={!isEditing} placeholder="代理人4" />
              </div>
              <div>
                <span className="text-xs text-gray-500">代理人5 (F905_15)</span>
                <Input value={proxy5} onChange={e => setProxy5(e.target.value)}
                  maxLength={8} disabled={!isEditing} placeholder="代理人5" />
              </div>
            </div>
          </div>

          {/* 使用權限部門 1~5 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">使用權限部門</label>
            <div className="grid grid-cols-5 gap-2">
              <div>
                <span className="text-xs text-gray-500">部門1 (F905_21)</span>
                <Input value={dept1} onChange={e => setDept1(e.target.value)}
                  maxLength={4} disabled={!isEditing} placeholder="部門1" />
              </div>
              <div>
                <span className="text-xs text-gray-500">部門2 (F905_22)</span>
                <Input value={dept2} onChange={e => setDept2(e.target.value)}
                  maxLength={4} disabled={!isEditing} placeholder="部門2" />
              </div>
              <div>
                <span className="text-xs text-gray-500">部門3 (F905_23)</span>
                <Input value={dept3} onChange={e => setDept3(e.target.value)}
                  maxLength={4} disabled={!isEditing} placeholder="部門3" />
              </div>
              <div>
                <span className="text-xs text-gray-500">部門4 (F905_24)</span>
                <Input value={dept4} onChange={e => setDept4(e.target.value)}
                  maxLength={4} disabled={!isEditing} placeholder="部門4" />
              </div>
              <div>
                <span className="text-xs text-gray-500">部門5 (F905_25)</span>
                <Input value={dept5} onChange={e => setDept5(e.target.value)}
                  maxLength={4} disabled={!isEditing} placeholder="部門5" />
              </div>
            </div>
          </div>
        </div>

        {/* Audit Fields (read-only) */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-6 space-y-1">
            <div>建立者: {current.f905_91} | 建立日期: {current.f905_92 || '-'}</div>
            <div>最後修改者: {current.f905_93}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default P9105Form;
